import { Link } from 'react-router-dom';
import { CheckCircle2, Play } from 'lucide-react';
import { cn } from '@hrms/ui';
import { HomeCard } from '../../../routes/home/HomeCard.js';
import { useLearnerHome } from '../hooks.js';
import { learnerCoursePath } from '../myLearning.js';
import { localizeText } from '../player/lessonLanguage.js';
import { useLessonLanguage } from '../player/useLessonLanguage.js';
import { CourseCover } from '../ui/index.js';
import { continueMetaLine, nextLessonLine } from './ContinueLearningHero.js';
import { LearningProgressBar } from './LearningProgressBar.js';

/**
 * Home's small "Continue learning" card (R15, PLAN §4.1): the course to pick up, its next lesson
 * and progress, one Continue. It reads the same query as My learning, so it costs nothing extra
 * after a visit there. Hidden when nothing is assigned and on any error — Home never shows a broken
 * or empty training tile. Placement and module/permission gating are Home's (`HomeGate`).
 */
export function ContinueLearningCard({ className }: { className?: string }) {
  const query = useLearnerHome();
  const { chosen: language } = useLessonLanguage();
  const home = query.data?.status === 200 ? query.data.body : undefined;
  if (!home || home.assignments.length === 0) return null;

  const entry = home.continueLearning;
  if (!entry) {
    const completed = home.assignments.filter((a) => a.status === 'completed').length;
    return (
      <HomeCard title="My learning" viewAll={{ to: '/learning', label: 'Open' }} className={className}>
        <p className="flex items-center gap-2 text-sm text-fg-muted">
          <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
          You&apos;re all caught up{completed > 0 ? ` — ${completed} ${completed === 1 ? 'course' : 'courses'} completed` : ''}.
        </p>
      </HomeCard>
    );
  }

  const { assignment } = entry;
  // In the language the learner last picked in the player (ADR 0039); the course's own otherwise.
  const task = localizeText(assignment.task, language);
  const next = nextLessonLine(entry.nextLesson, language);
  return (
    <HomeCard title="Continue learning" viewAll={{ to: '/learning', label: 'My learning' }} className={className}>
      <div className="flex items-center gap-3">
        <CourseCover
          title={task.title}
          category={task.category}
          coverImageUrl={task.coverImageUrl}
          size="sm"
          className="w-24 shrink-0 sm:w-28"
        />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold leading-snug text-fg">{task.title}</p>
          {next && <p className="truncate text-xs text-fg-muted">{next}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <LearningProgressBar
          percent={Number.parseFloat(assignment.progressPercent)}
          label={`${task.title} progress`}
        />
        <p className="text-xs tabular-nums text-fg-muted">{continueMetaLine(entry, new Date())}</p>
      </div>
      <Link
        to={{
          pathname: learnerCoursePath(assignment.id),
          search: entry.nextLesson ? `?lesson=${entry.nextLesson.id}` : '',
        }}
        className={cn(
          'mt-4 inline-flex h-9 items-center justify-center gap-2 self-start rounded-md bg-accent px-3.5 text-sm font-semibold text-fg-on-accent',
          'transition-colors hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        )}
      >
        <Play className="size-3.5 fill-current" aria-hidden="true" />
        Continue
      </Link>
    </HomeCard>
  );
}
